import { useId, useState, useEffect, useMemo, useRef } from 'react';
import {
  Tablet,
  ArrowLeft,
  Layers,
  Eye,
  Clock,
  User,
  ChevronRight,
  ChevronDown,
  X,
  Info,
  Trophy,
  Zap,
  Link2,
  WifiOff,
} from 'lucide-react';
import {
  banepadCourtList,
  courtDisplayName,
  banepadResolveCourt,
  banepadForceShouldClear,
  banepadHourlyStatus,
  banepadFormatLabel,
  banepadMatchClock,
  banepadScoreText,
  banepadBookingTag,
  banepadBookingRange,
  timeToMinutes,
  resolveBanepadAutoForce,
  TOURNAMENT_TYPES,
  getTournamentType,
} from '../../engine';
import { Storage } from '../../lib/storage';
import { useAsyncAction } from '../../lib/useAsyncAction';
import { useBanepadDevices } from '../../state/useBanepadDevices';
import { formatSilence, padStatusChip } from '../../lib/banepadHeartbeat';
import { BanepadCourtScreen } from './BanepadCourtScreen';
import { usePadSubmitPhase } from '../../state/usePadSubmitPhase';
import { BanepadPairModal } from './BanepadPairModal';
import { useT } from '../../i18n';
import { Dialog } from '../ui';
import { safeId } from '../ui/labelledControl';
import { TurneringsDashboard } from './TurneringsDashboard';
import { HIT_AREA_24 } from '../ui/hitArea';

export interface BanepadCentralViewProps {
  /** All bookings (drives every court's live status + day timelines). */
  bookings?: any[];
  /** Booking config (court list, per-court metadata). */
  bookingConfig?: any;
  /** Club info (fallback court list). */
  clubInfo?: any;
  /** The active tournament. */
  tournament?: any;
  /** Paused/parked tournaments (still listed on the banepad). */
  pausedTournaments?: any[];
  /** The active seriespill (league). */
  activeSeries?: any;
  /** Activity types (drives booking tags). */
  activities?: any[];
  /** Local planned (booker-picked) single matches — forceable + shown on pads. */
  plannedMatches?: any[];
  /** Opens the full tournament view for a given format. */
  onOpenTournamentView?: any;
  /** Opens the full seriespill view. */
  onOpenSeriesView?: any;
  /** Records a Padel24 result (tournamentId, matchId, s1, s2, source). */
  onRecordResult?: any;
  /** Records a KM (club championship) result (tournamentId, updated match). */
  onRecordKMResult?: any;
  onRecordKMLiveScore?: any;
  onRecordPadLiveScore?: any;
  /** Records a seriespill result (updated match). */
  onRecordSerieResult?: any;
  onRecordSerieLiveScore?: any;
  /** Records a single-match (enkeltmatch) result: (activity, { team1, team2, sets, playedAt }). */
  onRecordSingleMatchResult?: any;
  /** Persists the live (in-progress) single-match score: (activity, liveScore). */
  onLiveSingleMatchScore?: any;
  /** All players (resolves names/ELO in the rendered pads). */
  allPlayers?: any[];
  /** Back to the home view. */
  onBack?: any;
  /**
   * Adapts an Americano match shape into the slot-based shape CourtPadTab expects.
   * Helper passed in by the call-site; forwarded to <BanepadCourtScreen>. The call-site
   * must pass it.
   */
  americanoMatchToSlots: (m: any) => any;
  /**
   * The live `Padel24Engine` wiring object. Forwarded to <BanepadCourtScreen>
   * (→ the Padel24/Americano/Club pads).
   * (deviation: the monolith read this via closure.)
   */
  Padel24Engine: any;
  /**
   * Plays an end-sound by key — shared audio helper (Web Audio).
   * NOTE: forwarded to <BanepadCourtScreen>.
   * (deviation: the monolith read this via closure.)
   */
  playEndSound: (soundType?: string) => void;
}

// ── Central main screen — all courts live ──
/**
 * Banepad central dashboard — lists every court with a live status grid and a stacked preview
 * of the actual pad screens, lets staff force a tournament/series onto all pads, configures the
 * auto-force window around bookings, and opens a single court's pad in full size. Extracted
 * verbatim from the original monolith; markup/logic unchanged. Takes
 * `americanoMatchToSlots`, `Padel24Engine` and `playEndSound` as props, forwarded down
 * to <BanepadCourtScreen>.
 */
export function BanepadCentralView({
  bookings = [],
  bookingConfig = null,
  clubInfo = null,
  tournament = null,
  pausedTournaments = [],
  activeSeries = null,
  activities = [],
  plannedMatches = [],
  onOpenTournamentView = null,
  onOpenSeriesView = null,
  onRecordResult = null,
  onRecordKMResult = null,
  onRecordKMLiveScore = null,
  onRecordPadLiveScore = null,
  onRecordSerieResult = null,
  onRecordSerieLiveScore = null,
  onRecordSingleMatchResult = null,
  onLiveSingleMatchScore = null,
  allPlayers = [],
  onBack = null,
  americanoMatchToSlots,
  Padel24Engine,
  playEndSound,
}: BanepadCentralViewProps) {
  const [now, setNow] = useState(() => new Date());
  const [focusCourt, setFocusCourt] = useState(null);
  const [layout, setLayout] = useState('begge'); // 'begge' = grid + pads, 'grid' = grid only, 'pads' = pads only
  const [detail, setDetail] = useState(null); // { court, name } → popup showing who has booked
  const t = useT();
  const detailTitleId = safeId('banepad-detail-title', useId());
  const [pairCourt, setPairCourt] = useState(null); // { court, name } → pad-pairing modal
  const devices = useBanepadDevices(); // banepad pad-pairing registrations (PoC)
  const [dashboardMode, setDashboardMode] = useState(false); // show the turnerings-dashboard
  // Set when the dashboard is opened for a SPECIFIC tournament from the list
  // (rather than the generic button, which shows whatever is running).
  const [dashboardEventId, setDashboardEventIdRaw] = useState<any>(null);
  const setDashboardEventId = (id: any) => {
    setDashboardEventIdRaw(id);
    setDashboardMode(true);
  };
  const closeDashboard = () => {
    setDashboardMode(false);
    setDashboardEventIdRaw(null);
  };
  const [autoForceCollapsed, setAutoForceCollapsed] = useState(true); // minimised by default
  const [forceListCollapsed, setForceListCollapsed] = useState(true); // minimised by default
  const [forcedSessions, setForcedSessions] = useState(() => new Set()); // seriesIds forced live on the pads
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  // Forced sessions are persisted so they survive navigation/reload (and can be shared).
  useEffect(() => {
    (async () => {
      try {
        const raw = await Storage.get('banepad_forced_v1');
        if (raw?.value) {
          const arr = JSON.parse(raw.value);
          if (Array.isArray(arr)) setForcedSessions(new Set(arr));
        }
      } catch {
        /* deviation (lint): monolith's empty `catch {}` trips no-empty; comment added, behaviour unchanged. */
      }
    })();
  }, []);
  // deviation: monolith defines `toggleForce` but never calls it (dead code tolerated
  // under @ts-nocheck); renamed `_toggleForce` to satisfy lint (no-unused-vars). Verbatim otherwise.
  const _toggleForce = (seriesId) => {
    setForcedSessions((prev) => {
      const next = new Set(prev);
      if (next.has(seriesId)) next.delete(seriesId);
      else next.add(seriesId);
      try {
        Storage.set('banepad_forced_v1', JSON.stringify([...next]));
      } catch {
        /* deviation (lint): monolith's empty `catch {}` trips no-empty; comment added, behaviour unchanged. */
      }
      return next;
    });
  };
  // Tournament forced onto the pads (shown regardless of date/time). Persisted for reload.
  const [forcedTournamentId, setForcedTournamentId] = useState(null);
  // When the force was switched on (epoch ms) — drives the "how long it's been on"
  // timer in the (minimised) header. Persisted alongside the forced id.
  const [forcedSince, setForcedSince] = useState<number | null>(null);
  useEffect(() => {
    (async () => {
      try {
        const raw = await Storage.get('banepad_forced_tourn_v1');
        if (raw?.value) {
          const id = JSON.parse(raw.value);
          if (id) setForcedTournamentId(id);
        }
        const sraw = await Storage.get('banepad_forced_since_v1');
        if (sraw?.value) {
          const since = JSON.parse(sraw.value);
          if (since) setForcedSince(since);
        }
      } catch {
        /* deviation (lint): monolith's empty `catch {}` trips no-empty; comment added, behaviour unchanged. */
      }
    })();
  }, []);
  // AWAITED, AND THE FAILURE IS NOT SWALLOWED (#369).
  //
  // These were fire-and-forget `Storage.set` calls inside a `catch {}`, with the
  // toggle flipping first. That is backwards twice over: storage-first is the
  // rule for the client path (CLAUDE.md), and here it is also the honest
  // behaviour — THE PADS READ THIS FROM THE SERVER. Until the write lands,
  // nothing has changed for the courts, so a control that has already flipped is
  // telling the operator something that is not true yet.
  const persistForce = async (nextId: any, since: number | null) => {
    await Storage.set('banepad_forced_tourn_v1', JSON.stringify(nextId));
    await Storage.set('banepad_forced_since_v1', JSON.stringify(since));
  };
  const toggleForceTournament = async (id) => {
    const nextId = forcedTournamentId === id ? null : id;
    const since = nextId ? Date.now() : null;
    await persistForce(nextId, since);
    setForcedTournamentId(nextId);
    setForcedSince(since);
    // Force = manual override: the pads in the central view switch to the tournament.
    // No navigating away — each pad shows its own court.
  };
  // Unconditional clear (used by the auto-clear lifecycle below).
  const clearForce = async () => {
    await persistForce(null, null);
    setForcedTournamentId(null);
    setForcedSince(null);
  };

  // The force toggle's own state (#369): it waits for the write, refuses a
  // second press while in flight, and says so if the courts never got it.
  const forceToggle = useAsyncAction(toggleForceTournament);

  // Auto-force window: automatically show the event's pad on the courts it uses, within a
  // time window around the booking (x min before start, x min after end). The "after" buffer
  // only applies until results have been submitted if afterOnlyIfPending is on.
  const [autoForce, setAutoForce] = useState({
    enabled: true,
    minutesBefore: 30,
    minutesAfter: 30,
    afterOnlyIfPending: true,
  });
  const autoForceLoaded = useRef(false);
  useEffect(() => {
    (async () => {
      try {
        const raw = await Storage.get('banepad_autoforce_v1');
        if (raw?.value) {
          const v = JSON.parse(raw.value);
          if (v && typeof v === 'object') setAutoForce((a) => ({ ...a, ...v }));
        }
      } catch {
        /* deviation (lint): monolith's empty `catch {}` trips no-empty; comment added, behaviour unchanged. */
      }
      autoForceLoaded.current = true;
    })();
  }, []);
  useEffect(() => {
    if (!autoForceLoaded.current) return;
    try {
      Storage.set('banepad_autoforce_v1', JSON.stringify(autoForce));
    } catch (_e) {
      /* ignore */
    }
  }, [autoForce]);

  const courts = useMemo(
    () => banepadCourtList(bookingConfig, clubInfo),
    [bookingConfig, clubInfo],
  );
  const courtMeta = bookingConfig?.courtMeta || {};
  // Paired pads that have stopped reporting in (see lib/banepadHeartbeat).
  const offlinePads = useMemo(
    () =>
      courts
        .map((court) => ({ court, p: devices.presenceForCourt(court) }))
        .filter(({ p }) => p?.presence === 'offline')
        .map(({ court, p }) => ({
          court,
          name: courtDisplayName(court, courtMeta),
          silentMs: p!.silentMs,
        })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [courts, courtMeta, devices.presenceForCourt],
  );
  const typeTagPx = (tag, size = 9) =>
    tag ? (
      <span
        className="font-mono font-bold uppercase tracking-wider rounded px-1.5 py-0.5 flex-shrink-0"
        style={{
          fontSize: size,
          background: tag.color + '22',
          color: tag.color,
          border: `1px solid ${tag.color}55`,
          whiteSpace: 'nowrap',
        }}
      >
        {tag.label}
      </span>
    ) : null;
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const todayISO = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const clock = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;

  // Today's banepad tournaments/sessions — derived from reserved tournament bookings
  // (Padel24, KM, mini-tournaments, seriespill). Grouped per series/activity.
  // All tournaments that are ready to run or running — active (loaded) +
  // paused/parked. Shown in the banepad list regardless of date. "Force pad" switches
  // all the pads to the selected tournament immediately.
  const padTournaments = useMemo(() => {
    const run = ['ready', 'running', 'playing', 'finals'];
    const all = [tournament, ...(pausedTournaments || [])].filter(Boolean);
    const seen = new Set();
    const out = [];
    all.forEach((t) => {
      if (!t) return;
      const id = t.id || t.displayId;
      // The forced tournament is ALWAYS included — even if its status temporarily
      // falls outside the run filter after a result (otherwise the forced session loses
      // the pad and jumps back to the default view).
      const isForced = id && id === forcedTournamentId;
      if (!isForced && !run.includes(t.status)) return;
      if (!Array.isArray(t.matches) || t.matches.length === 0) return;
      if (!id || seen.has(id)) return;
      seen.add(id);
      out.push(t);
    });
    return out;
  }, [tournament, pausedTournaments, forcedTournamentId]);
  const forcedTournament = useMemo(() => {
    if (!forcedTournamentId) return null;
    // Look up in the FULL list (active + all paused), not in the filtered padTournaments —
    // so force survives even if status/filter temporarily changes after a result.
    const all = [tournament, ...(pausedTournaments || [])].filter(Boolean);
    return all.find((t) => (t.id || t.displayId) === forcedTournamentId) || null;
  }, [tournament, pausedTournaments, forcedTournamentId]);
  const effectiveTournament = forcedTournament || tournament;
  // A series can also be forced from the central view (its own flag — the series is resolved
  // separately from tournaments). forcedTournamentId can point at the series.
  const forcedSeries =
    activeSeries &&
    forcedTournamentId &&
    (activeSeries.id || activeSeries.displayId) === forcedTournamentId
      ? activeSeries
      : null;
  // Enkeltkamper are forced as ONE group (the SM_FORCE_ALL sentinel): every
  // enkeltmatch shows on its own booked court at once. Per court we look up the
  // enkeltmatch (activity via booking.seriesId, or local plan via plan.bookingId)
  // linked to a booking on that court today; the raw activity/plan goes to the
  // resolver (which normalises a plan via its bookingId).
  const SM_FORCE_ALL = '__ALL_SINGLE_MATCH__';
  const allSingleMatchForced = forcedTournamentId === SM_FORCE_ALL;
  const singleMatchForCourt = (court) => {
    if (!allSingleMatchForced) return null;
    const todays = (bookings || []).filter(
      (b) => Number(b.court) === Number(court) && b.date === todayISO,
    );
    for (const b of todays) {
      if (b.seriesId) {
        const act = (activities || []).find(
          (a) =>
            a.id === b.seriesId &&
            a.config?.tournamentType === 'single_match' &&
            a.status !== 'cancelled' &&
            a.status !== 'completed',
        );
        if (act) return act;
      }
      const plan = (plannedMatches || []).find(
        (p) => p.bookingId === b.id && p.status !== 'cancelled' && p.status !== 'completed',
      );
      if (plan) return plan;
    }
    return null;
  };
  // Find the event that should be auto-forced on a court right now (or null).
  //
  // ── ONE COPY OF THE WINDOW MATHS, SHARED WITH THE PADS (#663) ────
  //
  // This used to be ~30 lines duplicating `resolveBanepadCourtContext`'s internal
  // version line for line: the booking window ± buffer, the per-session override,
  // `afterOnlyIfPending`, and the midnight wrap. They agreed — and that is the half
  // where a divergence would actually hide, because it is the half with arithmetic
  // in it. The operator header below and the tablet now read the same answer by
  // construction rather than by both copies being maintained in step.
  //
  // THE SUPPRESSION STAYS HERE, because «manually forced» is this view's own state
  // and the shared resolver has no business deciding it.
  const autoForceForCourt = (court) => {
    if (forcedTournament || forcedSeries || allSingleMatchForced) return null; // manual force overrides auto
    return resolveBanepadAutoForce({
      court,
      tournament,
      pausedTournaments,
      activeSeries,
      bookings,
      autoForce,
      nowMin,
      todayISO,
    });
  };
  // Events that are LIVE on the pads right now purely because of their time window
  // (auto-vis), not a manual force. Deduped per event with the latest window end —
  // surfaced in the (minimised) header so you can see what's on and until when.
  const autoActiveEvents = (() => {
    if (forcedTournament || forcedSeries || allSingleMatchForced) return [];
    const map = new Map();
    courts.forEach((c) => {
      const af = autoForceForCourt(c);
      if (af?.event) {
        const id = af.event.id || af.event.displayId;
        const cur = map.get(id);
        const end = af.windowEnd ?? 0;
        if (!cur || end > cur.windowEnd) map.set(id, { event: af.event, windowEnd: end });
      }
    });
    return [...map.values()];
  })();
  // Auto-clear a manual force back to normal once the event is finished, or its
  // time window has passed (respects the auto-vis after-buffer / afterOnlyIfPending).
  // Off-window preview forces (no booking today) and forced enkeltkamper are left be.
  const forcedClearEvent = forcedTournament || forcedSeries;
  useEffect(() => {
    if (!forcedClearEvent) return;
    if (
      banepadForceShouldClear({
        event: forcedClearEvent,
        isSeries: !!forcedSeries,
        bookings,
        autoForce,
        nowMin,
        todayISO,
      })
    ) {
      void clearForce();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forcedClearEvent, forcedSeries, bookings, autoForce, nowMin, todayISO]);
  // Effective pad context per court: manual force > auto-force window > normal.
  const courtCtx = (court) => {
    if (forcedTournament || forcedSeries) {
      return {
        tournament: effectiveTournament,
        activeSeries,
        forceTournament: !!forcedTournament,
        forceSeries: !!forcedSeries,
        forceSingleMatch: null,
      };
    }
    // Forced enkeltkamper (group): each court shows its OWN enkeltmatch, others
    // resolve normally. So return the normal context plus this court's match.
    if (allSingleMatchForced) {
      return {
        tournament: effectiveTournament,
        activeSeries,
        forceTournament: false,
        forceSeries: false,
        forceSingleMatch: singleMatchForCourt(court),
      };
    }
    const af = autoForceForCourt(court);
    if (af) {
      if (af.isSeries)
        return {
          tournament: null,
          activeSeries: af.event,
          forceTournament: false,
          forceSeries: true,
          forceSingleMatch: null,
        };
      return {
        tournament: af.event,
        activeSeries: null,
        forceTournament: true,
        forceSeries: false,
        forceSingleMatch: null,
      };
    }
    return {
      tournament: effectiveTournament,
      activeSeries,
      forceTournament: false,
      forceSeries: false,
      forceSingleMatch: null,
    };
  };
  const serieForceEntry =
    activeSeries && Array.isArray(activeSeries.matches) && activeSeries.matches.length > 0
      ? { ...activeSeries, __isSeries: true }
      : null;
  // Single-match (enkeltmatch) activities that can be forced onto the pads. Only
  // those with a known linked court booking (so the pad knows where to show) and
  // not yet completed/cancelled.
  const padSingleMatches = useMemo(() => {
    const out: any[] = [];
    // Via-aktiviteter enkeltmatch (linked via booking.seriesId).
    (activities || [])
      .filter(
        (a) =>
          a.config?.tournamentType === 'single_match' &&
          a.status !== 'cancelled' &&
          a.status !== 'completed',
      )
      .forEach((a) => {
        const bk = (bookings || []).find((b) => b.seriesId === a.id);
        if (bk)
          out.push({
            id: a.id,
            __isSingleMatch: true,
            __booking: bk,
            __confirmed: (a.registrations || []).filter((r) => r.status === 'confirmed').length,
          });
      });
    // Local planned enkeltmatch (linked via plan.bookingId; booker picked all 4).
    (plannedMatches || [])
      .filter((p) => p.status !== 'cancelled' && p.status !== 'completed')
      .forEach((p) => {
        const bk = (bookings || []).find((b) => b.id === p.bookingId);
        if (bk) out.push({ id: p.id, __isSingleMatch: true, __booking: bk, __confirmed: 4 });
      });
    return out;
  }, [activities, plannedMatches, bookings]);
  // Enkeltkamper are grouped into ONE force row (instead of one per match) — it
  // forces every enkeltmatch onto its own court at once. NOTE: enkeltmatch is a
  // DEMO-only feature on the banepad; this whole group row should be removed from
  // the force list before production.
  const singleMatchGroup =
    padSingleMatches.length > 0
      ? [{ __isSingleMatchGroup: true, id: SM_FORCE_ALL, count: padSingleMatches.length }]
      : [];
  const forceList = [
    ...padTournaments,
    ...(serieForceEntry ? [serieForceEntry] : []),
    ...singleMatchGroup,
  ];
  /**
   * «SEND RESULTAT» REPORTS ON ITSELF HERE TOO (#557).
   *
   * `CourtPadCard` has said «Sender…» → «Registrert» since #216, for every
   * format — but only the PHYSICAL pad ever handed it a `submitStatus`, from
   * `BanepadDeviceShell`'s submission state machine. In this view nothing did,
   * so the phase was permanently `idle` and the button never moved. QA reported
   * it as «Nå skjer ikke noen endring», which is exactly what it was.
   *
   * This view is the portal's equivalent of that shell, so it owns the phase
   * the same way — same three states, same `PAD_RESULT_CONFIRM_MS`, so the
   * preview and the wall behave alike rather than merely similarly.
   */
  const { submitStatus, reporting } = usePadSubmitPhase();

  // Result from a pad → write to the tournament the pad is actually showing.
  const padRecordResult = (matchId, s1, s2, source) =>
    reporting(matchId, () => {
      const t = effectiveTournament;
      if (!t || !onRecordResult) return;
      onRecordResult(t.id || t.displayId, matchId, s1, s2, source);
    });
  const padRecordKMResult = (updatedMatch) =>
    reporting(updatedMatch?.id, () => {
      const t = effectiveTournament;
      if (!t || !onRecordKMResult) return;
      onRecordKMResult(t.id || t.displayId, updatedMatch);
    });
  const padRecordKMLiveScore = (updatedMatch) => {
    const t = effectiveTournament;
    if (!t || !onRecordKMLiveScore) return;
    onRecordKMLiveScore(t.id || t.displayId, updatedMatch);
  };
  const padRecordPadLiveScore = (matchId, liveScore) => {
    const t = effectiveTournament;
    if (!t || !onRecordPadLiveScore) return;
    onRecordPadLiveScore(t.id || t.displayId, matchId, liveScore);
  };
  const padRecordSerieResult = (updatedMatch) =>
    reporting(updatedMatch?.id, () => {
      if (!onRecordSerieResult) return;
      onRecordSerieResult(updatedMatch);
    });
  const padRecordSerieLiveScore = (updatedMatch) => {
    if (!onRecordSerieLiveScore) return;
    onRecordSerieLiveScore(updatedMatch);
  };
  const padRecordSingleMatchResult = (activity, data) => {
    if (!onRecordSingleMatchResult) return;
    onRecordSingleMatchResult(activity, data);
  };
  const padLiveSingleMatchScore = (activity, liveScore) => {
    if (!onLiveSingleMatchScore) return;
    onLiveSingleMatchScore(activity, liveScore);
  };
  // deviation: monolith defines `fmtMin` but never calls it (dead code tolerated under
  // @ts-nocheck); renamed `_fmtMin` to satisfy lint (no-unused-vars). Verbatim otherwise.
  const _fmtMin = (m) =>
    `${String(Math.floor((m % 1440) / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;

  // Each screen = one court. Tap a court → open the pad at full iPad size.
  const pinned = focusCourt != null ? Number(focusCourt) : null;
  if (pinned != null && courts.includes(pinned)) {
    const pctx = courtCtx(pinned);
    return (
      <div className="w-full mx-auto" style={{ maxWidth: 1120 }}>
        <BanepadCourtScreen
          court={pinned}
          courtMeta={courtMeta}
          bookings={bookings}
          activities={activities}
          plannedMatches={plannedMatches}
          tournament={pctx.tournament}
          activeSeries={pctx.activeSeries}
          now={now}
          forcedSessions={forcedSessions}
          forceTournament={pctx.forceTournament}
          forceSeries={pctx.forceSeries}
          forceSingleMatch={pctx.forceSingleMatch}
          onRecordResult={padRecordResult}
          submitStatus={submitStatus}
          onRecordKMResult={padRecordKMResult}
          onRecordKMLiveScore={padRecordKMLiveScore}
          onRecordPadLiveScore={padRecordPadLiveScore}
          onRecordSerieResult={padRecordSerieResult}
          onRecordSerieLiveScore={padRecordSerieLiveScore}
          onRecordSingleMatchResult={padRecordSingleMatchResult}
          onLiveSingleMatchScore={padLiveSingleMatchScore}
          allPlayers={allPlayers}
          americanoMatchToSlots={americanoMatchToSlots}
          Padel24Engine={Padel24Engine}
          playEndSound={playEndSound}
          onExit={() => setFocusCourt(null)}
          onOpenTournamentView={onOpenTournamentView}
          onOpenSeriesView={onOpenSeriesView}
        />
      </div>
    );
  }

  const statusOf = (court) => {
    const c = courtCtx(court);
    return banepadResolveCourt({
      court,
      bookings,
      tournament: c.tournament,
      activeSeries: c.activeSeries,
      activities,
      plannedMatches,
      nowMin,
      todayISO,
      forcedSessions,
      forceTournament: c.forceTournament,
      forceSeries: c.forceSeries,
      forceSingleMatch: c.forceSingleMatch,
    });
  };
  const busyCount = courts.filter((c) => {
    const s = statusOf(c);
    return s.mode === 'booking' || (s.mode === 'tournament' && s.current);
  }).length;

  // A white "card" with a collapsible header (chevron) — used for the central's
  // configuration sections so staff can minimise them. Rendered as a function
  // (not a component) so the body's inputs never remount on a parent re-render.
  const collapsibleCard = (
    icon: any,
    title: any,
    collapsed: boolean,
    onToggle: () => void,
    headerRight: any,
    body: any,
  ) => (
    <div className="rounded-xl p-3" style={{ background: '#ffffff', border: '1px solid #e5e7eb' }}>
      <div className="flex items-center justify-between gap-2">
        <button
          onClick={onToggle}
          className="flex items-center gap-2 min-w-0 flex-1 text-left"
          title={collapsed ? 'Vis' : 'Minimer'}
        >
          <ChevronDown
            size={14}
            style={{
              color: '#0e7490',
              flexShrink: 0,
              transition: 'transform .15s',
              transform: collapsed ? 'rotate(-90deg)' : 'none',
            }}
          />
          {icon}
          <span className="font-mono text-[11px] uppercase tracking-wider text-ink-2 truncate">
            {title}
          </span>
        </button>
        {headerRight}
      </div>
      {!collapsed && <div className="mt-2.5">{body}</div>}
    </div>
  );

  // ── Status chip for the (minimised) "Turneringer på banepad" header ──
  // Shows at a glance whether something is on the pads and why: a manual FORCE
  // (orange, with a live "how long it's been on" timer) or an AUTO-VIS window
  // (green, with the time it stays on until).
  const fmtElapsed = (ms: number) => {
    const s = Math.max(0, Math.floor(ms / 1000));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const ss = s % 60;
    return h > 0 ? `${h}t ${m}m` : `${m}:${String(ss).padStart(2, '0')}`;
  };
  const fmtMin = (min: number) =>
    `${String(Math.floor(min / 60) % 24).padStart(2, '0')}:${String(Math.round(min % 60)).padStart(2, '0')}`;
  const isManualForce = !!(forcedTournament || forcedSeries || allSingleMatchForced);
  const forcedStatusName =
    forcedTournament?.config?.name ||
    forcedTournament?.name ||
    forcedSeries?.config?.name ||
    (allSingleMatchForced ? 'Enkeltkamper' : 'Turneringen');
  const forceStatusChip = isManualForce ? (
    <span
      className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider px-2 py-1 rounded-full flex-shrink-0"
      style={{ background: '#ffedd5', color: '#9a3412', border: '1px solid #fdba74' }}
      title="Forced manuelt på banepaden"
    >
      <Zap size={11} /> <span className="truncate max-w-[140px]">{forcedStatusName}</span>
      {forcedSince != null && (
        <span className="tabular-nums">· {fmtElapsed(now.getTime() - forcedSince)}</span>
      )}
    </span>
  ) : autoActiveEvents.length > 0 ? (
    <span
      className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider px-2 py-1 rounded-full flex-shrink-0"
      style={{ background: '#dcfce7', color: '#166534', border: '1px solid #86efac' }}
      title="På automatisk fordi det er tidsrommet for turneringen"
    >
      ●{' '}
      <span className="truncate max-w-[150px]">
        {autoActiveEvents
          .map((a) => a.event.config?.name || a.event.name || 'Turnering')
          .join(', ')}
      </span>
      <span className="tabular-nums">
        · auto til {fmtMin(Math.max(...autoActiveEvents.map((a) => a.windowEnd)))}
      </span>
    </span>
  ) : null;

  if (dashboardMode) {
    return (
      <TurneringsDashboard
        courts={courts}
        statusFor={statusOf}
        courtName={(c) => courtDisplayName(c, courtMeta)}
        clock={clock}
        allPlayers={allPlayers}
        tournament={effectiveTournament}
        pausedTournaments={pausedTournaments}
        activeSeries={activeSeries}
        focusEventId={dashboardEventId}
        onBack={closeDashboard}
        onOpenCourt={(c) => {
          closeDashboard();
          setFocusCourt(c);
        }}
      />
    );
  }

  return (
    <div className="space-y-4">
      {/* Top bar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5">
          {onBack && (
            <button
              onClick={onBack}
              className="flex items-center gap-1.5 text-ink-2 hover:text-ink-1 transition-colors font-mono text-xs uppercase tracking-wider"
            >
              <ArrowLeft size={15} /> Hjem
            </button>
          )}
          <span className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-ink-3">
            <Tablet size={13} /> Banepad · sentral
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span
            className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider"
            style={{ color: '#0e7490' }}
          >
            <span className="relative flex h-2 w-2">
              <span
                className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75"
                style={{ background: '#22d3ee' }}
              ></span>
              <span
                className="relative inline-flex rounded-full h-2 w-2"
                style={{ background: '#06b6d4' }}
              ></span>
            </span>
            Live · {clock}
          </span>
          <span className="font-mono text-[11px] uppercase tracking-wider text-ink-3">
            {busyCount}/{courts.length} i bruk
          </span>
          <button
            onClick={() => setDashboardMode(true)}
            className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider px-3 py-1.5 rounded-full transition-colors"
            style={{ background: '#0e7490', color: '#ecfeff', fontWeight: 700 }}
          >
            <Trophy size={12} /> Dashboard
          </button>
        </div>
      </div>

      {/* Display filter: Begge (default) / Rutenett / Pads */}
      <div
        className="flex items-center gap-1 p-1 rounded-full self-start"
        style={{ background: '#ecfeff', border: '1px solid #cffafe', width: 'fit-content' }}
      >
        {[
          { id: 'begge', label: 'Begge', icon: Layers },
          { id: 'grid', label: 'Rutenett', icon: Tablet },
          { id: 'pads', label: 'Pads', icon: Eye },
        ].map((opt) => {
          const active = layout === opt.id;
          const Ico = opt.icon;
          return (
            <button
              key={opt.id}
              onClick={() => setLayout(opt.id)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full font-mono uppercase tracking-wider transition-colors"
              style={{
                fontSize: 10,
                fontWeight: 700,
                background: active ? '#06b6d4' : 'transparent',
                color: active ? '#ecfeff' : '#0e7490',
              }}
            >
              <Ico size={12} /> {opt.label}
            </button>
          );
        })}
      </div>

      {/* Auto-show pad around events — collapsible, placed ABOVE the force list. */}
      {collapsibleCard(
        <Clock size={14} style={{ color: '#0e7490', flexShrink: 0 }} />,
        'Auto-vis pad rundt hendelser',
        autoForceCollapsed,
        () => setAutoForceCollapsed((c) => !c),
        <button
          onClick={() => setAutoForce((a) => ({ ...a, enabled: !a.enabled }))}
          className="font-mono text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full flex-shrink-0 transition-colors"
          style={
            autoForce.enabled
              ? { background: '#dcfce7', color: '#166534', border: '1px solid #86efac' }
              : { background: '#f3f4f6', color: '#6b7280', border: '1px solid #e5e7eb' }
          }
        >
          {autoForce.enabled ? '● På' : 'Av'}
        </button>,
        autoForce.enabled ? (
          <div className="space-y-2.5">
            <p className="text-[11px] text-ink-3 leading-snug">
              Paden bytter automatisk til hendelsens skjerm på banene den bruker, i et vindu rundt
              bookingtiden — så folk ser hvor de skal spille og kan legge inn resultater.
            </p>
            <div className="flex items-center gap-4 flex-wrap">
              <label className="flex items-center gap-2 text-[12px] text-ink-2">
                <span>
                  Minutter <strong>før</strong> start
                </span>
                <input
                  type="number"
                  min="0"
                  value={autoForce.minutesBefore}
                  onChange={(e) =>
                    setAutoForce((a) => ({
                      ...a,
                      minutesBefore: Math.max(0, parseInt(e.target.value) || 0),
                    }))
                  }
                  className="px-2 py-1 rounded font-mono text-[12px] text-center"
                  style={{ width: 60, border: '1px solid #e5e7eb' }}
                />
              </label>
              <label className="flex items-center gap-2 text-[12px] text-ink-2">
                <span>
                  Minutter <strong>etter</strong> slutt
                </span>
                <input
                  type="number"
                  min="0"
                  value={autoForce.minutesAfter}
                  onChange={(e) =>
                    setAutoForce((a) => ({
                      ...a,
                      minutesAfter: Math.max(0, parseInt(e.target.value) || 0),
                    }))
                  }
                  className="px-2 py-1 rounded font-mono text-[12px] text-center"
                  style={{ width: 60, border: '1px solid #e5e7eb' }}
                />
              </label>
            </div>
            <label className="flex items-start gap-2 text-[12px] text-ink-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!autoForce.afterOnlyIfPending}
                onChange={(e) =>
                  setAutoForce((a) => ({ ...a, afterOnlyIfPending: e.target.checked }))
                }
                className="mt-0.5 flex-shrink-0"
                style={{ accentColor: '#0e7490' }}
              />
              <span className="leading-snug">
                «Etter slutt»-vinduet gjelder <strong>kun til resultatene er sendt inn</strong> —
                paden holdes oppe i påvente av resultater, og forsvinner straks alt på banen er
                registrert.
              </span>
            </label>
          </div>
        ) : null,
      )}

      {/* Tournaments on the banepad — collapsible force-pad card. */}
      {(padTournaments.length > 0 || serieForceEntry || padSingleMatches.length > 0) &&
        collapsibleCard(
          <Tablet size={14} style={{ color: '#0e7490', flexShrink: 0 }} />,
          'Turneringer på banepad',
          forceListCollapsed,
          () => setForceListCollapsed((c) => !c),
          forceStatusChip,
          <>
            <div className="flex flex-col gap-2">
              {forceList.map((t) => {
                const isSerie = !!t.__isSeries;
                const isSMGroup = !!t.__isSingleMatchGroup; // grouped enkeltkamper (demo)
                const tid = t.id || t.displayId;
                const tt = isSMGroup
                  ? TOURNAMENT_TYPES.single_match
                  : isSerie
                    ? TOURNAMENT_TYPES.seriespill
                    : getTournamentType(t.config);
                const forced = forcedTournamentId === tid;
                const running = isSMGroup
                  ? false
                  : isSerie
                    ? true
                    : ['running', 'playing', 'finals'].includes(t.status);
                const done = isSMGroup
                  ? 0
                  : (t.matches || []).filter((m) =>
                      isSerie ? m.score || m.completed : m.completed,
                    ).length;
                const total = isSMGroup ? 0 : (t.matches || []).length;
                const title = isSMGroup ? 'Enkeltkamper (kun demo)' : t.config?.name || tt.label;
                const subtitle = isSMGroup
                  ? `${t.count} ${t.count === 1 ? 'kamp' : 'kamper'} · vises på sine baner`
                  : isSerie
                    ? `${done}/${total} kamper spilt · ${t.pairs?.length || 0} par`
                    : `${running ? `${done}/${total} kamper spilt` : 'Klar til start'} · ${t.players?.length || 0} spillere`;
                return (
                  <div
                    key={tid}
                    className="flex items-center gap-3 rounded-lg px-3 py-2"
                    style={{
                      background: '#f9fafb',
                      border: forced ? '1.5px solid #f97316' : '1px solid #f0f0f2',
                    }}
                  >
                    <span
                      className="font-mono text-[9px] font-bold px-1.5 py-0.5 rounded text-white flex-shrink-0"
                      style={{ background: tt.color }}
                      title={tt.label}
                    >
                      {tt.abbrev}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] font-semibold text-ink-1 truncate">{title}</div>
                      <div className="font-mono text-[11px] text-ink-3">{subtitle}</div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {running ? (
                        <span
                          className="font-mono text-[10px] uppercase tracking-wider px-2 py-1 rounded"
                          style={{
                            background: '#dcfce7',
                            color: '#166534',
                            border: '1px solid #86efac',
                          }}
                        >
                          ● Pågår
                        </span>
                      ) : (
                        <span
                          className="font-mono text-[10px] uppercase tracking-wider px-2 py-1 rounded"
                          style={{
                            background: '#fef9c3',
                            color: '#854d0e',
                            border: '1px solid #fde047',
                          }}
                        >
                          Klar
                        </span>
                      )}
                      {/* Per-tournament dashboard — lets you open the board for an
                          event that is NOT the one currently running. Enkeltkamper
                          are a grouped pseudo-row with no tournament behind them. */}
                      {!isSMGroup && (
                        <button
                          onClick={() => setDashboardEventId(tid)}
                          className="font-mono text-[10px] uppercase tracking-wider px-2.5 py-1.5 rounded-lg transition-colors flex items-center gap-1"
                          style={{
                            background: 'transparent',
                            color: '#0e7490',
                            border: '1px solid #a5f3fc',
                          }}
                          title="Vis dashboard for denne turneringen"
                        >
                          <Trophy size={11} /> Dashboard
                        </button>
                      )}
                      <button
                        disabled={forceToggle.pending}
                        onClick={() => void forceToggle.run(tid)}
                        className="font-mono text-[10px] uppercase tracking-wider px-2.5 py-1.5 rounded-lg transition-colors"
                        style={
                          forced
                            ? { background: '#f97316', color: '#ffffff', fontWeight: 700 }
                            : {
                                background: 'transparent',
                                color: '#c2410c',
                                border: '1px solid #fdba74',
                              }
                        }
                      >
                        {forceToggle.pending ? 'Sender…' : forced ? 'Force på ✓' : 'Force pad'}
                      </button>
                      {forceToggle.error && (
                        <span
                          role="alert"
                          className="font-mono text-[10px] uppercase tracking-wider"
                          style={{ color: '#dc2626' }}
                        >
                          Nådde ikke padene
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            {(forcedTournament || forcedSeries) && (
              <p className="mt-2 text-[10px] text-ink-3">
                «{(forcedTournament || forcedSeries)?.config?.name || 'Turneringen'}» vises nå på
                alle paddene, uavhengig av dato. Trykk «Force på» igjen for å slå av.
              </p>
            )}
            {allSingleMatchForced && (
              <p className="mt-2 text-[10px] text-ink-3">
                Enkeltkampene vises nå på banepaden for banene de er booket på, uavhengig av tid
                (kun demo). Trykk «Force på» igjen for å slå av.
              </p>
            )}
          </>,
        )}

      {/* Pads that have gone silent — so staff hear it from Sentralen, not from players */}
      {(layout === 'begge' || layout === 'grid') && offlinePads.length > 0 && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-xl px-3 py-2.5"
          style={{ background: '#fff1f2', border: '1px solid #fecdd3', color: '#9f1239' }}
        >
          <WifiOff size={16} className="flex-shrink-0 mt-0.5" />
          <div className="text-[12px]">
            <span className="font-semibold">
              {offlinePads.length === 1
                ? 'En pad har mistet kontakten'
                : `${offlinePads.length} pads har mistet kontakten`}
            </span>
            {': '}
            {offlinePads
              .map((p) => `${p.name} (uten kontakt i ${formatSilence(p.silentMs)})`)
              .join(' · ')}
            . Sjekk strøm og nett på nettbrettet.
          </div>
        </div>
      )}

      {/* Court grid */}
      {(layout === 'begge' || layout === 'grid') && (
        <div className="space-y-2">
          {layout === 'begge' && (
            <div className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.18em] text-ink-3">
              <Tablet size={12} /> Rutenett
            </div>
          )}
          <div
            className="grid gap-3"
            style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))' }}
          >
            {courts.map((court) => {
              const s = statusOf(court);
              const name = courtDisplayName(court, courtMeta);
              const padReg = devices.registrationForCourt(court);
              const padPaired = padReg?.status === 'paired';
              const padPresence = padPaired ? devices.presenceForCourt(court) : null;
              const padChip = padStatusChip(padPaired, padPresence);
              const padOffline = padChip.offline;
              let badge, badgeBg, badgeColor, accent;
              if (s.mode === 'tournament') {
                const live = !!s.current;
                badge = live ? 'Live' : 'Neste';
                badgeBg = live ? '#65a30d' : '#e2e8f0';
                badgeColor = live ? '#f7fee7' : '#475569';
                accent = '#65a30d';
              } else if (s.mode === 'booking') {
                badge = 'Booket';
                badgeBg = '#ea580c';
                badgeColor = '#fff7ed';
                accent = '#c2410c';
              } else {
                badge = 'Ledig';
                badgeBg = '#dcfce7';
                badgeColor = '#15803d';
                accent = '#22c55e';
              }
              const isBooked = s.mode === 'booking';
              // Booked → click shows who booked it. Otherwise → open the pad.
              const onCard = () => (isBooked ? setDetail({ court, name }) : setFocusCourt(court));
              // Future availability (from now onwards) as a mini timeline — same
              // colors as the pad's "I dag" stripe, so the right-hand rail corresponds.
              const railCol = { idle: '#22c55e', booking: '#f97316', tournament: '#e6c668' };
              const futureRail = (() => {
                const hrs = banepadHourlyStatus(
                  court,
                  bookings,
                  effectiveTournament,
                  activeSeries,
                  nowMin,
                  todayISO,
                  9,
                  null,
                );
                const out = [];
                hrs.forEach((h) =>
                  (h.segs || []).forEach((sg) => {
                    const from = Math.max(sg.from, nowMin);
                    if (from >= sg.to) return;
                    const mins = sg.to - from;
                    const last = out[out.length - 1];
                    if (last && last.kind === sg.kind) last.mins += mins;
                    else out.push({ kind: sg.kind, mins });
                  }),
                );
                return out;
              })();
              return (
                /* THE CARD IS NOT A BUTTON ANY MORE, and it could not have
                   been one: it announced itself as a button, took focus, and
                   had no key handler at all — so a keyboard user could reach
                   it and not activate it (WCAG 2.1.1). It also CONTAINED two
                   more `role="button"` spans, which is invalid nesting that
                   only worked because they called `stopPropagation`.

                   Untangled rather than patched: the card keeps its pointer
                   shortcut (a plain div with `onClick`, so a tap anywhere
                   still works, exactly as before) and every action it offered
                   is now a real <button> INSIDE it — «Se hvem som booket» for
                   a booked court, «Koble pad», and «Åpne pad». Those are what
                   Tab reaches, and they cover both branches of `onCard`.

                   `role="presentation"` SAYS SO (#900): the tap anywhere is a
                   pointer convenience that duplicates those buttons, not a
                   control of its own, so it has no role to announce and no key
                   to answer. */
                <div
                  key={court}
                  role="presentation"
                  onClick={onCard}
                  className="relative overflow-hidden text-left rounded-xl p-4 transition-all hover:shadow-md cursor-pointer"
                  style={{
                    background: '#fff',
                    border: `1px solid ${s.mode === 'idle' ? '#e2e8f0' : accent + '55'}`,
                  }}
                >
                  {/* Future availability — mini timeline on the right edge (green=free, orange=booked, gold=tournament) */}
                  <div
                    className="absolute top-0 right-0 bottom-0 flex flex-col"
                    style={{ width: 5 }}
                    aria-hidden="true"
                  >
                    {futureRail.map((sg, i) => (
                      <div
                        key={i}
                        style={{ flexGrow: sg.mins, flexBasis: 0, background: railCol[sg.kind] }}
                      />
                    ))}
                  </div>
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <span
                      className="font-display font-semibold text-ink-1 truncate"
                      style={{ fontSize: 16 }}
                    >
                      {name}
                    </span>
                    <span
                      className="font-mono font-bold uppercase tracking-wider px-2 py-0.5 rounded-full flex-shrink-0"
                      style={{ fontSize: 9, background: badgeBg, color: badgeColor }}
                    >
                      {badge}
                    </span>
                  </div>
                  {s.mode === 'tournament' &&
                    (() => {
                      const m = s.current || s.next;
                      // Enkeltmatch: the live standing comes from the activity's
                      // liveScore (sets entered continuously on the pad), not m.score.
                      const smSets =
                        s.source === 'single_match'
                          ? (s.activity?.liveScore?.sets || []).filter((x) => x.t1 || x.t2)
                          : null;
                      // Tournament/series: prefer the in-progress liveScore (set
                      // continuously on the pad) over the finalised m.score. KM/serie
                      // store sets; Padel24/Americano store { score1, score2 }.
                      const tourLiveSets =
                        s.source !== 'single_match'
                          ? (m?.liveScore?.sets || []).filter((x) => x.t1 || x.t2)
                          : null;
                      const tourLivePts =
                        s.source !== 'single_match' && m?.liveScore?.score1 != null
                          ? `${m.liveScore.score1}-${m.liveScore.score2 ?? 0}`
                          : null;
                      const smScoreText =
                        smSets && smSets.length
                          ? smSets.map((x) => `${x.t1}-${x.t2}`).join('  ')
                          : tourLiveSets && tourLiveSets.length
                            ? tourLiveSets.map((x) => `${x.t1}-${x.t2}`).join('  ')
                            : tourLivePts;
                      // Enkeltmatch: who is playing (first names) — "Eide/Foss vs Olsen/Nilsen".
                      const firstName = (pid) =>
                        (allPlayers.find((p) => p.id === pid)?.name || '').split(' ')[0];
                      const smPlayers =
                        s.source === 'single_match'
                          ? (() => {
                              const c = (s.activity?.registrations || []).filter(
                                (r) => r.status === 'confirmed',
                              );
                              if (c.length < 4) return null;
                              return `${firstName(c[0].playerId)}/${firstName(c[1].playerId)} vs ${firstName(c[2].playerId)}/${firstName(c[3].playerId)}`;
                            })()
                          : null;
                      return (
                        <div>
                          <div
                            className="font-mono uppercase tracking-wider"
                            style={{ fontSize: 9, color: accent }}
                          >
                            {banepadFormatLabel(s.formatType)}
                          </div>
                          <div className="text-ink-1 font-medium truncate" style={{ fontSize: 13 }}>
                            {s.name}
                          </div>
                          {smPlayers && (
                            <div
                              className="text-ink-2 truncate"
                              style={{ fontSize: 11, marginTop: 1 }}
                            >
                              {smPlayers}
                            </div>
                          )}
                          <div className="flex items-center justify-between mt-1.5">
                            <span className="text-ink-3 font-mono" style={{ fontSize: 11 }}>
                              {s.current ? 'Pågår' : 'Neste'}
                              {banepadMatchClock(m) ? ` · ${banepadMatchClock(m)}` : ''}
                            </span>
                            {(smScoreText || banepadScoreText(m?.score)) && (
                              <span
                                className="font-mono font-bold tabular-nums text-ink-1"
                                style={{ fontSize: 14 }}
                              >
                                {smScoreText || banepadScoreText(m?.score)}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  {s.mode === 'booking' && (
                    <div>
                      <div className="text-ink-2 font-mono tabular-nums" style={{ fontSize: 12 }}>
                        {banepadBookingRange(s.current)}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span
                          className="text-ink-1 font-semibold truncate flex-1"
                          style={{ fontSize: 15 }}
                        >
                          {s.current.bookedBy?.name || s.current.title || 'Reservert'}
                        </span>
                        {typeTagPx(
                          banepadBookingTag(s.current, activities, activeSeries, plannedMatches),
                        )}
                      </div>
                      {s.next?.length > 0 && (
                        <div className="text-ink-3 mt-1.5 truncate" style={{ fontSize: 11 }}>
                          Neste:{' '}
                          <span className="font-mono">kl {banepadBookingRange(s.next[0])}</span> ·{' '}
                          {s.next[0].bookedBy?.name ||
                            s.next[0].title ||
                            banepadBookingTag(s.next[0], activities, activeSeries, plannedMatches)
                              ?.label ||
                            'Booking'}
                        </div>
                      )}
                    </div>
                  )}
                  {s.mode === 'idle' && (
                    <div className="text-ink-3" style={{ fontSize: 12 }}>
                      {s.next?.[0] ? (
                        <>
                          Neste:{' '}
                          <span className="font-mono text-ink-2">
                            kl {banepadBookingRange(s.next[0])}
                          </span>{' '}
                          ·{' '}
                          {s.next[0].bookedBy?.name ||
                            s.next[0].title ||
                            banepadBookingTag(s.next[0], activities, activeSeries, plannedMatches)
                              ?.label ||
                            'Booking'}
                        </>
                      ) : (
                        'Ingen bookinger i dag'
                      )}
                    </div>
                  )}
                  {/* items-start + a wrapping left cluster so that on a booked court
                      the "Se hvem som booket" + "Koble pad" chips wrap to a second
                      line instead of overflowing into (and overlapping) "Åpne pad"
                      on a narrow card. */}
                  <div className="flex flex-wrap items-start justify-between mt-2.5 gap-2">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0">
                      {isBooked && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDetail({ court, name });
                          }}
                          className="flex items-center gap-1 font-mono uppercase tracking-wider flex-shrink-0 text-left"
                          style={{ fontSize: 9, color: '#c2410c' }}
                        >
                          <User size={10} /> Se hvem som booket
                        </button>
                      )}
                      {/* Pad pairing entry point — opens the pairing modal for this court */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setPairCourt({ court, name });
                        }}
                        className={`${HIT_AREA_24} flex items-center gap-1 font-mono font-bold uppercase tracking-wider rounded-full px-2 py-0.5 flex-shrink-0 text-left`}
                        style={
                          padOffline
                            ? { fontSize: 9, background: '#ffe4e6', color: '#be123c' }
                            : padPaired
                              ? { fontSize: 9, background: '#cffafe', color: '#0e7490' }
                              : { fontSize: 9, background: '#f1f5f9', color: '#64748b' }
                        }
                        title={padChip.title}
                      >
                        {padOffline ? <WifiOff size={10} /> : <Link2 size={10} />} {padChip.label}
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFocusCourt(court);
                      }}
                      className={`${HIT_AREA_24} flex items-center gap-1 font-mono uppercase tracking-wider hover:text-ink-1 flex-shrink-0 text-left`}
                      style={{ fontSize: 9, color: '#94a3b8' }}
                    >
                      <Tablet size={10} /> Åpne pad <ChevronRight size={10} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Stacked preview — the actual pad screens, one below the other */}
      {(layout === 'begge' || layout === 'pads') && (
        <div className="space-y-3">
          {layout === 'begge' && (
            <div className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.18em] text-ink-3 pt-2">
              <Eye size={12} /> Forhåndsvisning av pads
            </div>
          )}
          <div className="flex flex-col items-center gap-6">
            {courts.map((court) => {
              const cc = courtCtx(court);
              return (
                <div key={court} className="w-full" style={{ maxWidth: 920 }}>
                  <BanepadCourtScreen
                    court={court}
                    /* The stacked preview is one pad per court on one page, so
                       the court names are h2s here. Opening a single court
                       (above) renders the pad as the whole page and keeps h1. */
                    headingLevel={2}
                    courtMeta={courtMeta}
                    bookings={bookings}
                    activities={activities}
                    plannedMatches={plannedMatches}
                    tournament={cc.tournament}
                    activeSeries={cc.activeSeries}
                    now={now}
                    forcedSessions={forcedSessions}
                    forceTournament={cc.forceTournament}
                    forceSeries={cc.forceSeries}
                    forceSingleMatch={cc.forceSingleMatch}
                    onRecordResult={padRecordResult}
                    submitStatus={submitStatus}
                    onRecordKMResult={padRecordKMResult}
                    onRecordKMLiveScore={padRecordKMLiveScore}
                    onRecordPadLiveScore={padRecordPadLiveScore}
                    onRecordSerieResult={padRecordSerieResult}
                    onRecordSerieLiveScore={padRecordSerieLiveScore}
                    onRecordSingleMatchResult={padRecordSingleMatchResult}
                    onLiveSingleMatchScore={padLiveSingleMatchScore}
                    allPlayers={allPlayers}
                    americanoMatchToSlots={americanoMatchToSlots}
                    Padel24Engine={Padel24Engine}
                    playEndSound={playEndSound}
                    preview
                    onOpenTournamentView={onOpenTournamentView}
                    onOpenSeriesView={onOpenSeriesView}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Honest sync note (no backend yet) */}
      <div
        className="rounded-lg px-3.5 py-2.5 flex items-start gap-2"
        style={{ background: '#f0fdff', border: '1px solid #cffafe' }}
      >
        <Info size={14} style={{ color: '#0e7490', marginTop: 1, flexShrink: 0 }} />
        <span className="text-ink-2" style={{ fontSize: 12 }}>
          Live-data oppdateres på denne enheten. Hver skjerm er ment for én bane — tapp en bane for
          å åpne paden i full størrelse.
        </span>
      </div>

      {/* Popup: who has booked (click a booked court in the grid) */}
      {detail &&
        (() => {
          const dayList = (bookings || [])
            .filter((b) => Number(b?.court) === Number(detail.court) && b?.date === todayISO)
            .slice()
            .sort(
              (a, b) =>
                timeToMinutes(a.startTime || '00:00') - timeToMinutes(b.startTime || '00:00'),
            );
          const isCurrent = (b) => {
            const sM = timeToMinutes(b.startTime || '00:00');
            let eM = timeToMinutes(b.endTime || b.startTime || '00:00');
            if (eM <= sM) eM += 1440;
            return nowMin >= sM && nowMin < eM;
          };
          const cur = dayList.find(isCurrent) || null;
          const curName = cur?.bookedBy?.name || cur?.title || 'Reservert';
          const phone = cur?.bookedBy?.phone || cur?.phone;
          const email = cur?.bookedBy?.email || cur?.email;
          return (
            // A `Dialog` (#900), named by the court. Escape and the backdrop
            // close it, and focus goes back to «Se hvem som booket».
            <Dialog
              labelledBy={detailTitleId}
              onClose={() => setDetail(null)}
              showClose={false}
              backdropClassName="fixed inset-0 z-50 flex items-center justify-center p-4"
              backdropStyle={{ background: 'rgba(2,12,20,0.55)' }}
              className="bg-white rounded-2xl w-full max-w-sm shadow-xl overflow-x-hidden overflow-y-auto max-h-[92vh]"
              style={{ maxHeight: 'calc(100dvh - 2rem)' }}
            >
              <div
                className="flex items-center justify-between px-5 py-3.5"
                style={{ background: '#ea580c' }}
              >
                <span className="flex items-center gap-2 text-white">
                  <Tablet size={15} />{' '}
                  <span
                    id={detailTitleId}
                    className="font-display font-bold"
                    style={{ fontSize: 16 }}
                  >
                    {detail.name}
                  </span>
                </span>
                {/* It had no name: an ✕ glyph says nothing. */}
                <button
                  type="button"
                  onClick={() => setDetail(null)}
                  aria-label={t('ui.dialog.close')}
                  className="text-white/80 hover:text-white"
                >
                  <X size={18} />
                </button>
              </div>
              <div className="px-5 py-4 space-y-3">
                {cur ? (
                  <div
                    className="rounded-xl p-3.5"
                    style={{ background: '#fff7ed', border: '1px solid #fed7aa' }}
                  >
                    <div
                      className="font-mono uppercase tracking-wider mb-1.5"
                      style={{ fontSize: 10, color: '#9a3412' }}
                    >
                      Spiller nå
                    </div>
                    <div className="flex items-center gap-2 mb-1">
                      <Clock size={14} style={{ color: '#c2410c' }} />
                      <span
                        className="font-mono tabular-nums font-semibold text-ink-1"
                        style={{ fontSize: 15 }}
                      >
                        {banepadBookingRange(cur)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <User size={14} style={{ color: '#c2410c' }} />
                      <span className="font-semibold text-ink-1 flex-1" style={{ fontSize: 15 }}>
                        {curName}
                      </span>
                      {typeTagPx(
                        banepadBookingTag(cur, activities, activeSeries, plannedMatches),
                        10,
                      )}
                    </div>
                    {cur.notes && (
                      <div className="text-ink-2 mt-1.5" style={{ fontSize: 13 }}>
                        {cur.notes}
                      </div>
                    )}
                    {(phone || email) && (
                      <div className="text-ink-3 mt-1.5 font-mono" style={{ fontSize: 12 }}>
                        {[phone, email].filter(Boolean).join(' · ')}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-ink-3" style={{ fontSize: 13 }}>
                    Ingen aktiv booking akkurat nå.
                  </div>
                )}
                {dayList.length > 0 && (
                  <div>
                    <div
                      className="font-mono uppercase tracking-wider mb-1.5 text-ink-3"
                      style={{ fontSize: 10 }}
                    >
                      I dag på banen
                    </div>
                    <div className="space-y-1 max-h-48 overflow-y-auto">
                      {dayList.map((b, i) => (
                        <div
                          key={i}
                          className="flex items-center gap-2.5 rounded-lg px-2.5 py-1.5"
                          style={{ background: isCurrent(b) ? '#fff7ed' : '#f8fafc' }}
                        >
                          <span
                            className="font-mono tabular-nums text-ink-2 flex-shrink-0"
                            style={{ fontSize: 12, minWidth: 92 }}
                          >
                            {banepadBookingRange(b)}
                          </span>
                          <span className="text-ink-1 truncate flex-1" style={{ fontSize: 13 }}>
                            {b.bookedBy?.name || b.title || 'Booking'}
                          </span>
                          {typeTagPx(
                            banepadBookingTag(b, activities, activeSeries, plannedMatches),
                            9,
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <button
                  onClick={() => {
                    const c = detail.court;
                    setDetail(null);
                    setFocusCourt(c);
                  }}
                  className="w-full flex items-center justify-center gap-1.5 rounded-full py-2.5 font-semibold"
                  style={{ background: '#0a0b10', color: '#e6fbff', fontSize: 14 }}
                >
                  <Tablet size={15} /> Åpne pad <ChevronRight size={15} />
                </button>
              </div>
            </Dialog>
          );
        })()}

      {/* Pad-pairing modal (opened from a court card's "Koble pad" chip) */}
      {pairCourt && (
        <BanepadPairModal
          court={pairCourt.court}
          courtName={pairCourt.name}
          registration={devices.registrationForCourt(pairCourt.court)}
          onCreate={() => devices.create(pairCourt.court, pairCourt.name)}
          onRevoke={(id) => devices.revoke(id)}
          onClose={() => setPairCourt(null)}
        />
      )}
    </div>
  );
}
